// Form sửa bối cảnh/thời đại (đợt 260804) — dùng chung cho màn truyện (bản gốc, có PIN)
// và Bank drawer (snapshot riêng của tập, không PIN).
// Logic thuần nằm ở `worldSettingUtils.ts` (không import antd) để test được dưới jest của react-scripts.
import React, { useEffect, useState } from 'react';
import { Button, Input, Space, Switch, Tooltip, Typography } from 'antd';
import { SaveOutlined, PushpinOutlined } from '@ant-design/icons';
import type { WorldSetting } from '../../types/film';
import { parseForbiddenInput, formatForbiddenForInput, hasUnsavedChange } from './worldSettingUtils';

const { Text } = Typography;

const FIELDS: { key: keyof WorldSetting; label: string; placeholder: string; rows?: number }[] = [
  { key: 'era', label: 'Era & culture', placeholder: 'Classical antiquity, Greek mythology' },
  { key: 'world', label: 'Setting', placeholder: 'Mount Olympus, palaces of the gods, the Aegean' },
  { key: 'architecture', label: 'Architecture & materials', placeholder: 'marble colonnades, obsidian, gold leaf, oil lamps', rows: 2 },
  { key: 'costume', label: 'Costume vocabulary', placeholder: 'chiton, peplos, himation, chlamys; fibula clasps; sandals', rows: 2 },
];

interface Props {
  value: WorldSetting | null;
  onSave: (next: WorldSetting) => Promise<void> | void;
  onCancel?: () => void;
  /** Hiện công tắc PIN — chỉ ở cấp TRUYỆN (PIN là chuyện của cả truyện, không phải của một tập). */
  showPin?: boolean;
  /** Bật sẵn PIN khi mở form: người đã ngồi sửa nghĩa là đã xem và chốt. Vẫn tắt được. */
  defaultPinned?: boolean;
  saving?: boolean;
}

const WorldSettingEditor: React.FC<Props> = ({ value, onSave, onCancel, showPin, defaultPinned, saving }) => {
  const [draft, setDraft] = useState<WorldSetting>({ ...(value || {}), ...(defaultPinned ? { pinned: true } : {}) });
  const [forbiddenText, setForbiddenText] = useState(formatForbiddenForInput(value?.forbidden));

  // Bản đã lưu đổi (refresh sau khi lưu, hoặc chuyển plan) → nạp lại vào form.
  useEffect(() => {
    setDraft({ ...(value || {}), ...(defaultPinned ? { pinned: true } : {}) });
    setForbiddenText(formatForbiddenForInput(value?.forbidden));
  }, [value, defaultPinned]);

  const current: WorldSetting = { ...draft, forbidden: parseForbiddenInput(forbiddenText) };
  const dirty = hasUnsavedChange(value, current);

  return (
    <Space direction="vertical" size={10} style={{ width: '100%' }}>
      {FIELDS.map((f) => (
        <div key={String(f.key)}>
          <Text type="secondary" style={{ fontSize: 12 }}>{f.label}</Text>
          {f.rows ? (
            <Input.TextArea
              rows={f.rows} placeholder={f.placeholder}
              value={(draft[f.key] as string) || ''}
              onChange={(e) => setDraft({ ...draft, [f.key]: e.target.value })}
            />
          ) : (
            <Input
              placeholder={f.placeholder}
              value={(draft[f.key] as string) || ''}
              onChange={(e) => setDraft({ ...draft, [f.key]: e.target.value })}
            />
          )}
        </div>
      ))}

      <div>
        <Text type="secondary" style={{ fontSize: 12 }}>
          Forbidden terms — things wrong for this period (one per line)
        </Text>
        <Input.TextArea
          rows={3} placeholder={'platinum\nbrilliant-cut\nFrench doors'}
          value={forbiddenText}
          onChange={(e) => setForbiddenText(e.target.value)}
        />
      </div>

      <Space>
        <Button
          type="primary" icon={<SaveOutlined />} loading={saving} disabled={!dirty}
          onClick={() => onSave(current)}
        >
          Save
        </Button>
        {onCancel && <Button onClick={onCancel} disabled={saving}>Cancel</Button>}
        {showPin && (
          <Tooltip title="When pinned, AI never overwrites this period — even if the chapters read differently">
            <Space size={4}>
              <PushpinOutlined style={{ color: draft.pinned ? '#fa8c16' : '#bbb' }} />
              <Switch
                size="small" checked={!!draft.pinned}
                onChange={(checked) => setDraft({ ...draft, pinned: checked })}
              />
              <Text type="secondary" style={{ fontSize: 12 }}>Pin</Text>
            </Space>
          </Tooltip>
        )}
        {dirty && <Text type="warning" style={{ fontSize: 12 }}>Unsaved changes</Text>}
      </Space>
    </Space>
  );
};

export default WorldSettingEditor;
